<script setup>
/* eslint-disable vue/v-on-event-hyphenation -- preserve exact Options API event casing */
import {
  computed,
  ref,
  watch,
  inject,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { contactAgendaName } from 'dashboard/helper/contactAgendaName';
import { aceleresContactDisplayName } from 'dashboard/helper/aceleresContactDisplayName';
import { indicadorDeCanal } from 'dashboard/helper/aceleresCanalIndicadores';
import { shouldShowMetaMessagingWindowExpiredNotice } from 'dashboard/helper/metaMessagingWindow';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import ConversationAvatarViewer from 'next/Conversation/ConversationAvatarViewer.vue';
import MessagePreview from './MessagePreview.vue';
import DraftPreview from './DraftPreview.vue';
import ReactionPreview from './ReactionPreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import ContactClassificationBadge from './ContactClassificationBadge.vue';
import FunilLabelCreateModal from './FunilLabelCreateModal.vue';
import FunilLabelDeleteModal from './FunilLabelDeleteModal.vue';
import { fetchManagedFunilLabels } from 'dashboard/api/aceleres/funilLabels';
import { useFunilStages } from 'dashboard/composables/useFunilStages';
import { useAdmin } from 'dashboard/composables/useAdmin';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import { getPointerViewportPosition } from 'dashboard/helper/dashboardZoom';
import VoiceCallStatus from './VoiceCallStatus.vue';
import RodizioCountdown from './RodizioCountdown.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import wootConstants from 'dashboard/constants/globals';
import {
  shouldPreserveAssignedUnreadForBox,
  viewerOwnsUnreadState,
} from 'dashboard/helper/unreadOwnership';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AssignmentTransferRequestsAPI from 'dashboard/api/aceleres/assignmentTransferRequests';
import AceleresOwnershipTransferModal from './AceleresOwnershipTransferModal.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';
import {
  CONTACT_CLASSIFICATION_ATTRIBUTE,
  resolveContactClassification,
  resolveContactClassificationSelection,
  shouldReconcileTeamNumberClassification,
} from 'dashboard/constants/contactClassifications';
import { activateMenu, deactivateMenu } from './contextMenu/activeMenuRegistry';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  // Aceleres: no historico do proprio contato o nome se repete em toda
  // linha e vira ruido. Padrao false — nenhum uso existente muda.
  hideContactName: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  activeAssigneeTab: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignSelf',
  'startConversation',
  'assignMine',
  'assignPersonal',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'togglePin',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

// Aceleres 2026-08-17: a foto do contato na lista de conversas era 49px e o
// pedido foi aumenta-la. O Avatar deriva TUDO desta medida — largura, altura,
// raio, o corpo das iniciais e a posicao do ponto de status —, entao mexer
// aqui basta: nao ha tamanho repetido em CSS. A unica regra que a lista impoe
// e a margem lateral (aceleres-overrides.css, escopo `.painel-atendimento`),
// que nao depende do tamanho.
const AVATAR_SIZE = 58;

// Aceleres 2026-08-17: a foto fica centrada na vertical pelo proprio flex
// (`self-center`), e nao por uma margem de topo fixa. Antes eram `mt-4`/`mt-8`
// conforme a linha do canal aparecesse ou nao — dois numeros calibrados a mao
// para a foto de 49px, que deixaram de acertar o centro quando ela passou a
// 58px. Com `self-center` o alinhamento acompanha sozinho o tamanho da foto e
// a altura do cartao, sem numero para recalibrar.

const PERSONAL_FUNNEL_LABELS = new Set([
  'nao-contactado',
  'ia',
  'contato-inicial',
  'desenvolvimento',
  'negociacao',
  'link-pagamento-gerado',
  'venda-feita',
  'nao-qualificado',
  'perdido',
  'sem-atendimento',
]);

const router = useRouter();
const store = useStore();
const { updateUISettings } = useUISettings();

const { getPlainText } = useMessageFormatter();

const conversationCard = ref(null);
const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });
const focusedContextMenuKey = ref(null);
const ownershipStatus = ref(null);
const showOwnershipModal = ref(false);
const ownershipSubmitting = ref(false);
const attendanceStatus = ref(null);
const showAttendanceModal = ref(false);
const attendanceSubmitting = ref(false);
const attendanceTargetAssigneeId = ref(null);
const classificationUpdating = ref(false);
const contextMenuRequestId = ref(0);
const contextMenuOwner = Symbol('conversation-context-menu');
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const avatarViewer = ref(null);
// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  contextMenuRequestId.value += 1;
  deactivateMenu(contextMenuOwner);
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  showOwnershipModal.value = false;
  showAttendanceModal.value = false;
  avatarViewer.value = null;
  attendanceTargetAssigneeId.value = null;
  ownershipStatus.value = null;
  attendanceStatus.value = null;
  contextMenu.value = { x: null, y: null };
  focusedContextMenuKey.value = null;
};

watch(() => props.chat.id, resetState);
onBeforeUnmount(() => {
  contextMenuRequestId.value += 1;
  deactivateMenu(contextMenuOwner);
});

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const currentUser = useMapGetter('getCurrentUser');
const inboxId = computed(() => props.chat.inbox_id);
const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

// Aceleres: nome do vendedor responsável exibido no card, com fallback
// explícito quando a conversa está na fila de novos (sem responsável) —
// evita que o nome da instância seja lido como identificação do vendedor.
const assigneeDisplayName = computed(
  () => assignee.value.name || 'Sem vendedor responsável'
);

const sender = computed(() => chatMetadata.value.sender || {});

const senderId = computed(() => sender.value.id);

const currentContact = computed(() => {
  const contact = senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : null;

  // A resposta da conversa já traz sender. Usá-lo enquanto o store de
  // contatos termina de hidratar evita cards sem nome ou avatar no primeiro
  // paint (e também cobre conversas legadas sem contato associado).
  if (!contact?.id) return sender.value;

  const contactAttributes =
    contact.custom_attributes || contact.customAttributes || {};
  const hasHydratedClassification = Object.prototype.hasOwnProperty.call(
    contactAttributes,
    CONTACT_CLASSIFICATION_ATTRIBUTE
  );

  return hasHydratedClassification ? contact : sender.value;
});

// O nome sai pela agenda da conexão desta conversa. Sem isso, um número salvo
// por dois vendedores com nomes diferentes mostraria nos dois cards o nome da
// conversa que hidratou o store por último. Ver helper/contactAgendaName.
const contactName = computed(() =>
  aceleresContactDisplayName(
    contactAgendaName(
      currentContact.value,
      props.chat?.inbox_id,
      'Contato indisponível'
    ) || 'Contato indisponível'
  )
);

const contactClassification = computed(() =>
  resolveContactClassification(currentContact.value)
);
const isCommercialContact = computed(
  () => contactClassification.value.value === 'commercial_lead'
);

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const ownsUnreadState = computed(() => {
  const inboxAttributes = inbox.value?.additional_attributes || {};
  return viewerOwnsUnreadState({
    currentUserId: currentUserId.value,
    assigneeId: assignee.value?.id,
    // In Outros/Pessoais the backend intentionally exposes the assignee's
    // unread state to managers and supervisors. Keep the green indicator in
    // the card instead of hiding it because the assignee is another user.
    preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
      props.activeAssigneeTab
    ),
    personalInboxAttributes: {
      ...inboxAttributes,
      connection_type:
        inboxAttributes.connection_type || props.chat?.inbox_connection_type,
      owner_chatwoot_user_id:
        inboxAttributes.owner_chatwoot_user_id ||
        props.chat?.inbox_owner_chatwoot_user_id,
    },
    personalConversationOwnerId:
      props.chat?.additional_attributes?.personal_owner_chatwoot_user_id,
  });
});

const unreadCount = computed(() => {
  if (!ownsUnreadState.value) {
    return 0;
  }

  return props.chat.unread_count || 0;
});

const hasUnread = computed(() => unreadCount.value > 0);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

// Aceleres: como no WhatsApp, o que ficou escrito ou anexado e ainda nao foi
// enviado passa na frente da ultima mensagem no preview da lista.
const conversationDraft = computed(() =>
  store.getters['draftMessages/getDraftSummary'](props.chat?.id)
);

// O rascunho continua identificado quando a conversa é aberta novamente, para
// o cartão não sair do bloco superior antes de o conteúdo ser enviado ou
// apagado (regra em draftMessages/hasVisibleDraft).
const hasDraft = computed(() =>
  store.getters['draftMessages/hasVisibleDraft'](props.chat?.id)
);

// Aceleres 2026-08-18: a ultima reacao da conversa, guardada pelo backend em
// `additional_attributes.last_reaction` (ver Aceleres::ConversationReactionPreview).
// Reagir nao cria mensagem nenhuma, entao sem isto a lista continuava mostrando
// a mesma previa de antes.
const lastReaction = computed(() => {
  const reaction = props.chat?.additional_attributes?.last_reaction;
  return reaction?.emoji ? reaction : null;
});

// A reacao so passa na frente da mensagem quando e mais NOVA que ela. O
// contrario acontece o tempo todo: alguem reage a uma mensagem antiga e, logo
// depois, chega mensagem nova — dali em diante a lista tem de voltar a mostrar
// a mensagem, como no WhatsApp. Os dois carimbos estao em segundos.
const showReactionPreview = computed(() => {
  if (!lastReaction.value) return false;

  const reactedAt = Number(lastReaction.value.at) || 0;
  const messageAt = Number(lastMessageInChat.value?.created_at) || 0;
  return reactedAt >= messageAt;
});

// Aceleres: preview do ultimo mensaje no hover (title nativo do Chrome).
// Hover nao marca como lido (makeMessagesRead so roda no click).
const unreadPreview = computed(() => {
  const msg = lastMessageInChat.value;
  if (!msg) return '';
  const subject = msg.content_attributes?.email?.subject;
  const text = getPlainText(subject || msg.content || '').trim();
  return text;
});

const rodizioAssignedAt = computed(() => {
  // Aceleres: timer só enquanto o lead ainda circula no rodízio — conversa
  // ou não-aberta não mostram countdown.
  const attrs = props.chat?.additional_attributes || {};
  if (attrs.rodizio_locked || attrs.rodizio_exhausted) return null;
  if (props.chat?.status && props.chat.status !== 'open') return null;
  return attrs.rodizio_assigned_at || null;
});

const rodizioIdleMins = computed(() => {
  const attrs = props.chat?.additional_attributes || {};
  const value = Number(attrs.rodizio_idle_minutes || attrs.rodizio_idle_mins);
  return Number.isFinite(value) && value > 0 ? value : null;
});

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const isTrainingChat = computed(() =>
  isTrainingConversation(props.chat, currentUser.value)
);

const inboxConnectionType = computed(
  () =>
    props.chat.inbox_connection_type ||
    inbox.value?.additional_attributes?.connection_type ||
    ''
);

const pendingOwnershipRequest = computed(
  () => ownershipStatus.value?.pending_request || null
);

const currentPortfolioOwner = computed(
  () => ownershipStatus.value?.current_owner || null
);

const canRequestOwnership = computed(
  () => ownershipStatus.value?.can_request === true
);

const ownershipAgents = computed(() => {
  return ownershipStatus.value?.eligible_targets || [];
});

const pendingAttendanceRequest = computed(
  () => attendanceStatus.value?.pending_request || null
);

const attendanceAgents = computed(
  () => attendanceStatus.value?.eligible_targets || []
);

const currentAttendant = computed(
  () => attendanceStatus.value?.current_assignee || null
);

// Indicador de origem da conversa. O card mostra só o ícone do canal; o nome
// completo (que distingue WhatsApp Oficial de Corporativo) vive no tooltip.
//
// O mapa saiu daqui para `helper/aceleresCanalIndicadores` quando a tela de
// carteirização passou a desenhar o mesmo ícone: canal novo entra lá, uma vez,
// e aparece nas duas telas.

const origemCanal = computed(() => {
  // meta.channel vem no próprio payload da conversa e cobre o intervalo em que
  // o store de inboxes ainda não hidratou — sem ele o primeiro paint da lista
  // sai sem ícone de canal.
  const ct = inbox.value?.channel_type || props.chat.meta?.channel;
  return indicadorDeCanal(ct);
});

const isMetaMessagingWindowExpired = computed(() => {
  const channelType = inbox.value?.channel_type || props.chat.meta?.channel;
  const expiresAt = Number(props.chat.messaging_window_expires_at);
  const isOutsideWindow =
    props.chat.can_reply === false ||
    (Number.isFinite(expiresAt) &&
      expiresAt > 0 &&
      Date.now() / 1000 >= expiresAt);

  return shouldShowMetaMessagingWindowExpiredNotice({
    channelType,
    isOutsideWindow,
  });
});

// Estado operacional atual da IA. O atributo atendido_ia permanece reservado
// ao filtro histórico.
const iaAtiva = computed(() => {
  const v = props.chat.custom_attributes?.ia_ativa_agora;
  return v === true || v === 'true' || v === 1 || v === '1';
});

// Aceleres: bolinha da etapa do funil, ao lado do icone do canal.
//
// A conversa carrega o SLUG da etapa entre as etiquetas; o nome legivel e a
// cor vem do funil da empresa (`useFunilStages`), porque variam por empresa e
// o Chatwoot nao os conhece. Sem etapa casada nao ha bolinha — melhor nada do
// que uma cor inventada.
const { load: loadFunilStages, stageForLabels } = useFunilStages(accountId);
const { isAdmin } = useAdmin();

onMounted(() => loadFunilStages());

const etapaFunil = computed(() => stageForLabels(props.chat.labels));

/**
 * Geometria da bolinha em estilo inline, de proposito.
 *
 * Como classe ela saiu 29x16 na tela — uma pilula, nao um circulo. Dentro de
 * uma linha flex basta uma regra que nao case (o id de escopo muda a cada
 * edicao do componente) ou uma base flexivel para a largura virar automatica e
 * o circulo esticar. `flex: 0 0 <medida>` prende as duas dimensoes e o inline
 * ganha de qualquer folha.
 */
const ETAPA_DOT_SIZE = '15px';

const etapaDotStyle = computed(() => ({
  backgroundColor: etapaFunil.value?.color,
  width: ETAPA_DOT_SIZE,
  height: ETAPA_DOT_SIZE,
  minWidth: ETAPA_DOT_SIZE,
  minHeight: ETAPA_DOT_SIZE,
  maxWidth: ETAPA_DOT_SIZE,
  maxHeight: ETAPA_DOT_SIZE,
  flex: `0 0 ${ETAPA_DOT_SIZE}`,
  // O `!important` aqui nao e exagero, e necessario. Medido em 21/08/2026:
  // um script de dashboard injetado em runtime traz, desde 29/06,
  //   .acl-conversation-card span[role="img"] { padding-left:13px !important }
  // para afastar o AVATAR do cartao. O seletor e largo e apanha qualquer
  // span[role="img"] do cartao — inclusive esta bolinha. Com box-sizing
  // border-box, 26px de padding contra 12px de largura esticam a caixa para
  // 26px, e o circulo virava pilula (medido: 26x12).
  //
  // Inline com !important ganha de folha de estilo com !important, por regra
  // de cascata. Tirar o role="img" tambem resolveria, mas custaria o rotulo
  // que o leitor de ecra anuncia.
  padding: '0 !important',
  boxSizing: 'border-box',
  borderRadius: '9999px',
  // Anel discreto para a bolinha nao sumir no fundo quando a etapa for clara.
  boxShadow: 'inset 0 0 0 1px rgb(0 0 0 / 12%)',
  display: 'inline-block',
}));

/**
 * O tooltip da bolinha identifica somente a etapa do funil.
 */
const etapaTooltip = computed(() =>
  etapaFunil.value ? `Etapa do funil: ${etapaFunil.value.nome}` : ''
);

const IA_INDICATOR_LABEL = 'Atendimento com IA';
// Mesmo motivo do rotulo da IA: em atributo o texto cru e recusado pelo
// `vue/no-bare-strings-in-template`, e o tooltip e o `aria-label` do alfinete
// tem de dizer exatamente a mesma coisa.
const PINNED_LABEL = 'Conversa fixada';

// Sem etapa casada no funil nao ha bolinha: melhor nada do que uma cor
// inventada. Ver o comentario de `etapaFunil`.
const showEtapaDot = computed(
  () => !!etapaFunil.value && isCommercialContact.value
);

const showOrigemBadges = computed(
  () =>
    !!origemCanal.value ||
    isMetaMessagingWindowExpired.value ||
    showEtapaDot.value ||
    (iaAtiva.value && isCommercialContact.value)
);

// Aceleres 2026-09-01: na caixa Pessoais o cartao nao leva a linha de origem
// (canal, etapa do funil, IA, Numero da Equipe, etiquetas e SLA). A caixa vem
// da aba ativa, que a ChatList ja passa por `activeAssigneeTab`
// (ACELERES_TAB_BOXES: `personal` -> `pessoais`); os cartoes renderizados fora
// da lista, sem essa prop, continuam a mostrar a linha.
const isPessoaisBox = computed(
  () => props.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.PERSONAL
);

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    !!inbox.value?.id &&
    [
      wootConstants.STATUS_TYPE.OPEN,
      wootConstants.STATUS_TYPE.RESOLVED,
    ].includes(props.chat?.status)
  );
});

const showConcludedStatusBadge = computed(
  () => props.chat?.status === wootConstants.STATUS_TYPE.RESOLVED
);

const showMetaSection = computed(() => {
  return showInboxName.value || props.showAssignee;
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const isPersonalConversation = computed(() => {
  const attrs = props.chat.additional_attributes || {};
  const inboxAttrs = inbox.value?.additional_attributes || {};

  return (
    isAceleresIndividualOwn(inboxAttrs.connection_type) ||
    Number(attrs.personal_owner_chatwoot_user_id) > 0 ||
    Number(attrs.personal_origin_chatwoot_user_id) > 0 ||
    Number(attrs.owner_chatwoot_user_id) > 0
  );
});

const visibleConversationLabels = computed(() => {
  const labels = Array.isArray(props.chat.labels) ? props.chat.labels : [];
  if (!isPersonalConversation.value) return labels;

  return labels.filter(label => !PERSONAL_FUNNEL_LABELS.has(label));
});

const showLabelsSection = computed(() => {
  return (
    visibleConversationLabels.value.length > 0 ||
    (hasSlaPolicyId.value && isCommercialContact.value)
  );
});

// Aceleres 04/09/2026: o CONTEUDO da linha de origem (canal, etapa, IA, selo de
// classificacao, "Concluido" e etiquetas) continua fora da caixa Pessoais. A
// prioridade, que em 04/09/2026 desceu para esta mesma linha, NAO segue essa
// regra: ela vinha da linha de cima (`showMetaSection`), que existe em
// Pessoais, e some-la seria uma perda que ninguem pediu. Por isso a linha
// aparece tambem so por causa da prioridade, e cada elemento do conteudo antigo
// carrega este guarda em vez de o deixar no `v-if` da linha.
const showOrigemRowContent = computed(
  () =>
    !isPessoaisBox.value && (showOrigemBadges.value || showLabelsSection.value)
);

// O desfecho é informado separadamente da lista de etiquetas. As etiquetas
// permanecem apenas informativas e não alteram o estado da conversa.
const conclusionResult = computed(
  () => props.chat?.additional_attributes?.conclusion_result || null
);

const isVendaPerdida = computed(() => {
  return conclusionResult.value === 'sale_lost';
});

const isVendaRealizada = computed(() => conclusionResult.value === 'sale_won');

// Aceleres 2026-09-04: "Digitando..." na linha da previa, como no WhatsApp.
// A store de digitacao e alimentada pelo ActionCable da CONTA inteira
// (`onTypingOn` em helper/actionCable.js), entao o cartao de qualquer conversa
// consegue ler o estado -- nao so o da conversa aberta.
//
// `type !== 'user'` e o mesmo filtro do ConversationHeader: separa o cliente do
// proprio atendente, que tambem entra nessa lista quando digita no editor.
const clientTypists = computed(() => {
  const userList =
    store.getters['conversationTypingStatus/getUserList'](props.chat?.id) || [];
  return userList.filter(user => user?.type !== 'user');
});

const isClientTyping = computed(() => clientTypists.value.length > 0);

// Num grupo o contato da conversa e o grupo inteiro, entao o nome de quem
// digita vem num campo proprio do evento (ver helper/actionCable.js). Fora de
// grupo ele nao existe e o rotulo continua sendo so "Digitando...".
const typingParticipantName = computed(
  () =>
    clientTypists.value
      .map(user => String(user?.typingParticipantName || '').trim())
      .find(Boolean) || ''
);

const messagePreviewClass = computed(() => {
  return [
    // Aceleres 2026-08-17: a linha da previa aparece em QUATRO lugares -- previa
    // de mensagem, rascunho, chamada de voz (VoiceCallStatus.vue) e o texto de
    // "sem mensagens". Os quatro repetiam a mesma string de classes, entao
    // mexer na escala exigia acertar os quatro sem esquecer nenhum. As medidas
    // passam a sair daqui, que e o unico ponto por onde todos ja passavam.
    'my-0 mx-2 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap',
    // Um degrau abaixo do nome do contato, como no WhatsApp: o nome esta em
    // `text-base` (16px) e a previa em `text-sm` (14px). O degrau e o que
    // impede as duas linhas de competirem pela mesma atencao.
    'text-sm leading-5 h-5',
    // Aceleres 2026-08-18: cor pedida para a linha da previa. Fica FORA do
    // condicional de nao-lida para nao repetir a mesma cor nos dois ramos --
    // o que distingue a nao-lida aqui e o peso, nao a cor. O par escuro e o
    // espelho do valor pedido (branco a 60%): preto a 60% sobre fundo escuro
    // seria ilegivel.
    // Aceleres 2026-09-04: enquanto o cliente digita, a linha troca de cor em
    // vez de ganhar uma classe a mais. Duas `text-*` do Tailwind no mesmo
    // elemento nao se resolvem pela ordem do atributo -- quem vence e a que
    // aparece depois na folha -- entao a cor tem de ser exclusiva.
    isClientTyping.value
      ? 'acl-typing-preview'
      : 'text-[#00000099] dark:text-[#ffffff99]',
    hasUnread.value ? 'font-medium' : '',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

// Aceleres: modo seleção (WhatsApp-style) injetado pelo ChatList — quando
// ativo, clique no card alterna seleção em vez de navegar.
const aclSelectionMode = inject('aclSelectionMode', ref(false));
const canOpenAvatar = computed(
  () => isMobileViewport.value && !!currentContact.value?.thumbnail && !props.hideThumbnail
);
const onToggleSelect = e => {
  if (e) e.stopPropagation();
  if (props.selected) {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('selectConversation', props.chat.id, inbox.value.id);
  }
};

const onAvatarClick = event => {
  if (!canOpenAvatar.value) return;

  event.stopPropagation();
  if (aclSelectionMode.value) {
    onToggleSelect();
    return;
  }

  avatarViewer.value = {
    src: currentContact.value.large_thumbnail || currentContact.value.thumbnail,
    thumbnail: currentContact.value.thumbnail,
    name: contactName.value,
  };
};

const onCardClick = async e => {
  if (aclSelectionMode.value) {
    onToggleSelect(e);
    return;
  }
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  // Abrir pelo cartão registra a leitura de quem clicou, seja ele o
  // responsável, o dono da inbox pessoal (rodízio pode ter deixado outro
  // vendedor no assignee) ou quem só acompanha. O corte é por usuário, então
  // nenhuma dessas leituras mexe no não-lido de outra pessoa; e o número da
  // caixa Outros continua sendo o do vendedor por decisão do backend, não por
  // um gate aqui.
  if (Number(props.chat.unread_count) > 0) {
    // Mark as read without blocking navigation to the conversation.
    store
      .dispatch('markMessagesRead', {
        id: props.chat.id,
        auto: true,
        conversation: props.chat,
      })
      .catch(() => {});
  }

  store.dispatch('previewActiveChat', { data: props.chat });
  if (props.chat?.dataFetched !== true) {
    store.dispatch('getConversation', props.chat.id);
  }
  return router.push({ path });
};

const onAvatarOpenChat = () => {
  avatarViewer.value = null;
  onCardClick({});
};

const onAvatarOpenContact = async () => {
  avatarViewer.value = null;
  await onCardClick({});
  await nextTick();
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const fetchOwnershipStatus = async (conversationId, requestId = null) => {
  try {
    const { data } =
      await ConversationTransferRequestsAPI.status(conversationId);
    if (
      Number(props.chat.id) === Number(conversationId) &&
      (requestId === null || contextMenuRequestId.value === requestId)
    ) {
      ownershipStatus.value = data?.payload || null;
    }
  } catch {
    if (requestId === null || contextMenuRequestId.value === requestId) {
      ownershipStatus.value = null;
    }
  }
};

const fetchAttendanceStatus = async (conversationId, requestId = null) => {
  try {
    const { data } = await AssignmentTransferRequestsAPI.status(conversationId);
    if (
      Number(props.chat.id) === Number(conversationId) &&
      (requestId === null || contextMenuRequestId.value === requestId)
    ) {
      attendanceStatus.value = data?.payload || null;
    }
  } catch {
    if (requestId === null || contextMenuRequestId.value === requestId) {
      attendanceStatus.value = null;
    }
  }
};

// Titulos que esta funcionalidade criou — so neles o menu mostra o caixote.
const deletableTitles = ref([]);
const labelToDelete = ref(null);

/**
 * Busca ao ABRIR o menu de contexto, nao ao montar o cartao: sao centenas de
 * cartoes na lista e o menu abre um de cada vez. Buscar aqui e uma chamada por
 * abertura, com o dado sempre fresco.
 */
const loadDeletableTitles = async (requestId = null) => {
  if (!isAdmin.value) return;
  try {
    const titles = await fetchManagedFunilLabels();
    if (requestId === null || contextMenuRequestId.value === requestId) {
      deletableTitles.value = titles;
    }
  } catch (error) {
    // Sem a lista ninguem fica com caixote — melhor do que oferecer apagar
    // uma etiqueta que talvez nao possa ser apagada.
    if (requestId === null || contextMenuRequestId.value === requestId) {
      deletableTitles.value = [];
    }
  }
};

const closeContextMenu = () => {
  contextMenuRequestId.value += 1;
  deactivateMenu(contextMenuOwner);
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
  focusedContextMenuKey.value = null;
};

const openContextMenuAt = (position, focusedMenuKey = null) => {
  if (!props.enableContextMenu || isTrainingChat.value) return;
  if (showContextMenu.value) return;

  const conversationId = props.chat.id;
  const requestId = contextMenuRequestId.value + 1;
  contextMenuRequestId.value = requestId;
  ownershipStatus.value = null;
  attendanceStatus.value = null;
  contextMenu.value.x = position.x;
  contextMenu.value.y = position.y;
  focusedContextMenuKey.value = focusedMenuKey;
  activateMenu({ owner: contextMenuOwner, close: closeContextMenu });
  emit('contextMenuToggle', true);
  showContextMenu.value = true;

  const backgroundTasks = [];
  if (!focusedMenuKey) {
    backgroundTasks.push(
      fetchOwnershipStatus(conversationId, requestId),
      fetchAttendanceStatus(conversationId, requestId)
    );
  }
  if (!focusedMenuKey || focusedMenuKey === 'label') {
    backgroundTasks.push(loadDeletableTitles(requestId));
  }
  Promise.all(backgroundTasks);
};

const openContextMenu = e => {
  e.preventDefault();
  e.stopPropagation();
  // Aceleres: o menu e `position: fixed`, logo o ponto tem de ser da VIEWPORT.
  // `pageX`/`pageY` incluem o scroll do documento e empurravam o menu para fora
  // do ecra; alem disso `pageX || clientX` descartava um clique legitimo em x=0.
  openContextMenuAt(getPointerViewportPosition(e));
};

const openContextMenuFromTrigger = (e, focusedMenuKey) => {
  if (!props.enableContextMenu || isTrainingChat.value) return;

  e.preventDefault();
  e.stopPropagation();
  const trigger = e.currentTarget;
  const bounds = trigger?.getBoundingClientRect?.();
  const position = bounds
    ? { x: bounds.left, y: bounds.bottom }
    : getPointerViewportPosition(e);

  openContextMenuAt(position, focusedMenuKey);
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

// Quem pode transferir direto é decisão do backend
// (AssignmentsController#transfer_requires_approval?), não da tela. O menu do
// cartão chegou a decidir sozinho, olhando `can_request` do endpoint de
// solicitações — régua mais estreita, que não conhece os casos em que o
// backend libera a transferência direta (conversa sem responsável, conversa
// pessoal / inbox individual_own, auto-atribuição). Numa conversa pessoal o
// header transferia e este menu abria um pedido ao gestor que normalmente nem
// tinha destinatário elegível: o vendedor escolhia o colega e nada acontecia.
//
// Agora o menu tenta a transferência como o header faz e só cai para o pedido
// ao gestor quando o backend responde 403 `requires_transfer_request`.
const openAttendanceRequestModal = async agent => {
  await fetchAttendanceStatus(props.chat.id);

  if (pendingAttendanceRequest.value) {
    useAlert(
      'Já existe uma transferência deste atendimento aguardando o gestor'
    );
    return;
  }

  if (!attendanceAgents.value.length) {
    useAlert('Nenhum vendedor disponível para receber este atendimento');
    return;
  }

  attendanceTargetAssigneeId.value = attendanceAgents.value.some(
    target => Number(target.id) === Number(agent?.id)
  )
    ? agent.id
    : null;
  showAttendanceModal.value = true;
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id], {
    onRequiresTransferRequest: openAttendanceRequestModal,
  });
  closeContextMenu();
};

const submitAttendanceRequest = async ({ targetAssigneeId, justification }) => {
  attendanceSubmitting.value = true;
  try {
    const { data } = await AssignmentTransferRequestsAPI.create({
      conversationId: props.chat.id,
      targetAssigneeId,
      justification,
    });
    showAttendanceModal.value = false;
    attendanceTargetAssigneeId.value = null;
    await fetchAttendanceStatus(props.chat.id);
    useAlert(
      data?.already_pending
        ? 'Já existe uma transferência deste atendimento aguardando o gestor'
        : 'Solicitação de transferência enviada ao gestor'
    );
  } catch (error) {
    await fetchAttendanceStatus(props.chat.id);
    if (pendingAttendanceRequest.value) {
      showAttendanceModal.value = false;
      useAlert('Solicitação de transferência enviada ao gestor');
      return;
    }

    useAlert(
      error?.response?.data?.error ||
        'Não foi possível solicitar a transferência do atendimento'
    );
  } finally {
    attendanceSubmitting.value = false;
  }
};

const onAssignSelf = () => {
  emit('assignSelf', props.chat.id);
  closeContextMenu();
};

const onStartConversation = () => {
  emit('startConversation', props.chat.id);
  closeContextMenu();
};

const onAssignMine = () => {
  emit('assignMine', props.chat.id);
  closeContextMenu();
};

const onAssignPersonal = () => {
  emit('assignPersonal', props.chat.id);
  closeContextMenu();
};

const onRequestOwnershipTransfer = async () => {
  closeContextMenu();
  await fetchOwnershipStatus(props.chat.id);

  if (!ownershipAgents.value.length) {
    useAlert('Nenhum outro vendedor disponível para receber a carteirização');
    return;
  }

  showOwnershipModal.value = true;
};

const submitOwnershipRequest = async ({ targetAssigneeId, justification }) => {
  ownershipSubmitting.value = true;
  try {
    const { data } = await ConversationTransferRequestsAPI.create({
      conversationId: props.chat.id,
      targetAssigneeId,
      justification,
    });
    showOwnershipModal.value = false;
    await fetchOwnershipStatus(props.chat.id);
    useAlert(
      data?.already_pending
        ? 'Já existe uma alteração de carteirização aguardando autorização'
        : 'Solicitação de alteração de carteirização enviada ao gestor'
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível solicitar a alteração de carteirização'
    );
  } finally {
    ownershipSubmitting.value = false;
  }
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onClassifyContact = async classification => {
  const contactId = Number(currentContact.value?.id);
  if (!contactId || classificationUpdating.value) return;

  const nextClassification = resolveContactClassificationSelection(
    currentContact.value,
    classification
  );
  const reconcileTeamNumber = shouldReconcileTeamNumberClassification(
    currentContact.value,
    classification
  );
  if (
    nextClassification === contactClassification.value.value &&
    !reconcileTeamNumber
  ) {
    closeContextMenu();
    return;
  }

  classificationUpdating.value = true;
  try {
    if (reconcileTeamNumber) {
      await TeamNumbersAPI.mark(contactId);
      await store.dispatch('contacts/show', { id: contactId });
    } else {
      await store.dispatch('contacts/update', {
        id: contactId,
        customAttributes: {
          [CONTACT_CLASSIFICATION_ATTRIBUTE]: nextClassification,
        },
      });
    }
    closeContextMenu();
    useAlert(`Contato classificado como ${contactClassification.value.label}`);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível alterar a classificação do contato'
    );
  } finally {
    classificationUpdating.value = false;
  }
};

// Aceleres: criar etiqueta sem sair da lista.
//
// O modal fica AQUI, e nao dentro do menu de contexto, porque o menu e
// posicionado por coordenada e fecha ao primeiro clique fora — um campo de
// texto dentro dele nao sobreviveria ao primeiro caractere.
const showCreateLabelModal = ref(false);
const accountLabels = useMapGetter('labels/getLabels');
const onRequestDeleteLabel = label => {
  closeContextMenu();
  labelToDelete.value = label;
};

const onLabelDeleted = ({ title }) => {
  labelToDelete.value = null;
  deletableTitles.value = deletableTitles.value.filter(t => t !== title);
  store.dispatch('labels/get');
};

const onCreateLabel = () => {
  closeContextMenu();
  showCreateLabelModal.value = true;
};

const onLabelCreated = label => {
  showCreateLabelModal.value = false;
  onAssignLabel(label);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const togglePin = () => {
  emit('togglePin', props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};

const applyAclOrigemLayout = () => {
  const root = conversationCard.value;
  if (!root) return;

  root.querySelectorAll('.acl-origem-row').forEach(row => {
    row.style.position = 'relative';
    row.style.paddingRight = '24px';
    row.style.display = 'flex';
    row.style.flexWrap = 'wrap';
    row.style.alignItems = 'center';
    row.style.alignContent = 'flex-start';
    // 6px, o mesmo do `gap-1.5` da classe e do grupo `.acl-origem-icons`, para
    // a distancia entre os indicadores ser uma so. E este `style` inline que
    // manda: ele ganha da classe, e enquanto aqui esteve `4px` mudar a classe
    // no template nao alterava NADA na tela (medido por CDP em 04/09/2026 —
    // `class` com `gap-1.5` e `getComputedStyle(row).gap` a dizer `4px`).
    row.style.gap = '6px';

    // Aceleres 04/09/2026: uma folha injetada em runtime traz
    //   .acl-conversation-card span[role="img"] { padding-left/right: 13px !important }
    // (existe para afastar a FOTO do cartao, ver o comentario de `etapaDotStyle`),
    // e o seletor apanha todos os indicadores desta linha, que sao spans com
    // `role="img"`: canal, etapa do funil, IA e Numero da Equipe ficavam com
    // 13px de cada lado — 26px de largura para 12px de desenho — enquanto a
    // bandeira de prioridade, que nao leva `role`, ficava sem nada. Dai a
    // distancia desigual. Zera-se aqui, inline e com `important`, porque e o
    // unico jeito de ganhar com certeza de um `!important` de folha externa
    // cuja ordem de injecao nao controlamos (a bolinha da etapa ja se defendia
    // assim). O escopo e so esta linha: a foto do cartao continua com o seu.
    row.querySelectorAll('[role="img"]').forEach(icone => {
      // O selo textual precisa de respiro: zerar seu padding encolhe
      // o balao junto com a fonte e deixa as letras nas bordas.
      const padding = icone.classList.contains('acl-meta-window-badge')
        ? '7px'
        : '0';
      icone.style.setProperty('padding-left', padding, 'important');
      icone.style.setProperty('padding-right', padding, 'important');
    });
  });

  root.querySelectorAll('.acl-origem-row button').forEach(btn => {
    btn.style.setProperty('display', 'inline-flex', 'important');
    btn.style.setProperty('visibility', 'visible', 'important');
    btn.style.setProperty('pointer-events', 'auto', 'important');
    btn.style.setProperty('position', 'absolute', 'important');
    btn.style.setProperty('top', '0', 'important');
    btn.style.setProperty('right', '0', 'important');
    btn.style.setProperty('margin', '0', 'important');
    btn.style.setProperty('z-index', '3', 'important');
    btn.style.setProperty('width', '20px', 'important');
    btn.style.setProperty('height', '20px', 'important');
    btn.removeAttribute('aria-hidden');
    btn.tabIndex = 0;
  });
};

onMounted(() => {
  nextTick(() => applyAclOrigemLayout());
});

onUpdated(() => {
  nextTick(() => applyAclOrigemLayout());
});
</script>

<template>
  <div
    ref="conversationCard"
    class="acl-conversation-card relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-t-0 border-b-0 border-l-0 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active animate-card-select bg-n-background border-n-weak my-1 rounded-md':
        isActiveChat,
      'bg-n-slate-2': selected,
      'bg-n-amber-1/30': chat.is_pinned,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- Aceleres: check verde à esquerda do avatar quando em modo seleção
         (WhatsApp-style). Substitui o overlay de checkbox no próprio avatar. -->
    <button
      v-if="aclSelectionMode && !hideThumbnail"
      type="button"
      class="acl-sel-check"
      :class="{ 'acl-sel-check--on': selected }"
      :aria-pressed="selected"
      :aria-label="selected ? 'Desmarcar' : 'Selecionar'"
      @click.stop="onToggleSelect"
    >
      <svg
        v-if="selected"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </button>
    <!-- Aceleres 2026-08-17: o `self-center` vive NESTE div, e nao no Avatar.
         Quem e filho do flex do cartao e este wrapper; posto no Avatar, o
         `align-self` nao tinha a quem obedecer (o wrapper nao e flex) e a foto
         subia para o topo da linha. -->
    <component
      :is="canOpenAvatar ? 'button' : 'div'"
      class="relative self-center"
      :class="{
        'acl-mobile-conversation-avatar-trigger border-0 bg-transparent p-0':
          canOpenAvatar,
      }"
      :type="canOpenAvatar ? 'button' : undefined"
      :aria-label="canOpenAvatar ? `Ver foto de ${contactName}` : undefined"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
      @click="onAvatarClick"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="contactName"
        :src="currentContact.thumbnail"
        :size="AVATAR_SIZE"
        :status="currentContact.availability_status"
        class="avatar-conversa"
        hide-offline-status
        rounded-full
      />
    </component>
    <div :title="unreadPreview" class="px-0 py-3 flex-1 min-w-0">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-center gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center whitespace-nowrap"
            :class="
              assignee.name
                ? 'truncate max-w-[170px] sm:max-w-[210px]'
                : 'text-n-slate-9 flex-shrink-0'
            "
            :title="assigneeDisplayName"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assigneeDisplayName }}
          </span>
        </div>
      </div>
      <div
        v-if="!hideContactName"
        class="flex items-center gap-1 mx-2 flex-1 min-w-0 ltr:pr-16 rtl:pl-16"
      >
        <h4
          class="conversation--user text-base my-0 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap min-w-0 text-n-slate-12 font-normal"
        >
          {{ contactName }}
        </h4>
      </div>
      <p
        v-if="isClientTyping"
        key="typing-preview"
        :class="messagePreviewClass"
      >
        {{
          typingParticipantName
            ? $t('CHAT_LIST.TYPING_PREVIEW_BY', { name: typingParticipantName })
            : $t('CHAT_LIST.TYPING_PREVIEW')
        }}
      </p>
      <VoiceCallStatus
        v-else-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <DraftPreview
        v-else-if="hasDraft && !isActiveChat"
        key="draft-preview"
        :draft="conversationDraft"
        :class="messagePreviewClass"
      />
      <ReactionPreview
        v-else-if="showReactionPreview"
        key="reaction-preview"
        :reaction="lastReaction"
        :class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        :class="messagePreviewClass"
      />
      <p v-else key="no-messages" :class="messagePreviewClass">
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <!-- Aceleres 2026-09-02: o alfinete da conversa fixada SAIU de junto do
             nome do contato e passou para a direita da linha da previa,
             encostado ao contador de nao lidas — a leitura do WhatsApp. Um por
             cartao: ter os dois lugares ao mesmo tempo mostrava dois alfinetes.
             Ele nao entra na propria linha da previa porque esta coluna e
             `absolute` e ficaria por cima dele; partilhando a linha do
             contador, os dois recuam juntos e nada se sobrepoe. -->
        <div
          class="acl-card-meta-row relative flex items-center justify-end gap-1 mt-1"
        >
          <!-- Aceleres 2026-09-02: a seta do hover mudou de sitio. Vinha do
               canto inferior direito do cartao (`bottom: 12px`) e passou para
               ESTA linha, a DIREITA do alfinete e no mesmo tamanho dele — os
               14px vem do `size-3.5` que os dois partilham, e nao de um numero
               escrito no CSS, para nao voltarem a divergir. Fica antes do
               alfinete no DOM e depois dele no ecra: e `absolute`, portanto a
               ordem aqui nao decide nada — quem decide e o `inset-inline-end`
               no CSS.

               E DECORATIVA por decisao do pedido: nao tem handler, nao para o
               evento e nao e focavel, portanto o clique nela cai no
               `onCardClick` do cartao e abre a conversa como qualquer outro
               ponto. Por isso e um `span` com `aria-hidden`, e nao um `button`
               — anunciar um botao que nada faz seria pior do que nao o anunciar.

               Continua `absolute`, contra esta linha (`inset-inline-end: 0`),
               e nao um item do flex. A diferenca importa: como item ela
               reservaria 14px permanentemente, e numa conversa sem alfinete e
               sem nao-lidas esta linha tem altura zero — ganhar 14px de altura
               empurraria o `RodizioCountdown` para baixo em parte da lista. Fora
               do fluxo, a altura da linha nao muda; o que se move no hover e so
               o recuo horizontal do alfinete e do contador, pelo
               `padding-inline-end` que abre lugar para a seta (ver o CSS).

               Esconde-se por `opacity`, nunca por `v-if` nem por `display`, pela
               mesma razao de sempre: o layout tem de ficar parado no hover. E
               continua sem existir no telemovel (`isMobileViewport`), onde nao
               ha hover — a guarda NAO pode ser `@media (hover: none)`, que casa
               tambem com o Chromium headless e apagaria a seta na verificacao. -->
          <span
            v-if="!aclSelectionMode && !isMobileViewport"
            class="acl-card-arrow i-lucide-chevron-down size-3.5 text-n-slate-11"
            aria-hidden="true"
            title=""
          />
          <span
            v-if="chat.is_pinned"
            v-tooltip.top="PINNED_LABEL"
            class="i-lucide-pin size-3.5 shrink-0 text-n-slate-11"
            role="img"
            :aria-label="PINNED_LABEL"
          />
          <span
            class="shadow-lg rounded-full text-[11px] font-semibold w-5 h-5 text-white bg-[#1daa61] inline-flex items-center justify-center leading-none"
            :class="hasUnread ? 'block' : 'hidden'"
          >
            {{ unreadCount }}
          </span>
        </div>
        <RodizioCountdown
          v-if="rodizioAssignedAt && isCommercialContact"
          :assigned-at="rodizioAssignedAt"
          :idle-mins="rodizioIdleMins"
          compact
          class="mt-1 ltr:ml-auto rtl:mr-auto"
        />
      </div>
      <div
        v-if="showOrigemRowContent || !!chat.priority"
        class="acl-origem-row flex items-center flex-wrap gap-1.5 mt-1 mb-0 mx-2"
      >
        <!-- Aceleres 04/09/2026: os indicadores desta linha (canal, etapa,
             IA, Numero da Equipe e prioridade) tem todos 16px e a mesma
             distancia entre si. A distancia so fica igual porque a linha
             (`acl-origem-row`) e este grupo usam o MESMO `gap-1.5`: a
             prioridade nao pode entrar aqui dentro — a caixa Pessoais leva a
             prioridade mas nao leva canal, etapa, IA nem Numero da Equipe, e
             alargar o `v-if` deste span para a acolher faria os outros
             aparecerem la. -->
        <span
          v-if="
            showOrigemRowContent && (origemCanal || showEtapaDot || iaAtiva)
          "
          class="acl-origem-icons inline-flex items-center gap-1.5 h-[20px]"
        >
          <span
            v-if="origemCanal"
            v-tooltip.top="origemCanal.label"
            class="shrink-0 rounded-full"
            :class="[
              origemCanal.kind === 'oficial' ||
              origemCanal.kind === 'corporativo'
                ? 'size-[17px]'
                : 'size-4',
              origemCanal.icon,
              origemCanal.color,
            ]"
            role="img"
            :aria-label="origemCanal.label"
          />
          <span
            v-if="isMetaMessagingWindowExpired"
            v-tooltip.top="$t('CONVERSATION.FOOTER.SOCIAL_WINDOW_BADGE')"
            class="acl-meta-window-badge inline-flex h-[20px] items-center rounded-full bg-[#FEF3C7] px-2 text-[9px] font-semibold leading-none text-[#92400E] md:text-[11px]"
            role="img"
            data-testid="meta-window-expired-card-badge"
            :aria-label="$t('CONVERSATION.FOOTER.SOCIAL_WINDOW_BADGE')"
          >
            {{ $t('CONVERSATION.FOOTER.SOCIAL_WINDOW_BADGE') }}
          </span>
          <!-- Aceleres: etapa atual do funil. Cor da etapa, nome no hover.

               Esta bolinha ocupou o lugar do selo de texto "Nao atendido", que
               vivia mais abaixo nesta mesma linha e saiu em 24/08/2026. O
               estado do atendimento continua correto no servidor
               (`attendance_started`, ver Aceleres::AttendanceMarker) e move a
               conversa entre as caixas, mas deixou de ter desenho no cartao.

               `title=""` nao e descuido. O bloco de conteudo do cartao carrega
               um `:title="unreadPreview"` (a previa da ultima mensagem), e o
               tooltip NATIVO do navegador vale para todos os descendentes.
               Sem isto, parar o rato na bolinha abria dois baloes ao mesmo
               tempo: a previa da mensagem e a etiqueta. O title vazio no
               proprio elemento corta a heranca e deixa so o nosso. -->
          <span
            v-if="showEtapaDot"
            v-tooltip.top="etapaTooltip"
            class="acl-funil-etapa-dot"
            :style="etapaDotStyle"
            role="img"
            title=""
            :aria-label="etapaTooltip"
          />
          <span
            v-if="iaAtiva && isCommercialContact"
            v-tooltip.top="IA_INDICATOR_LABEL"
            class="i-lucide-sparkles size-4 shrink-0 text-n-amber-11"
            role="img"
            :aria-label="IA_INDICATOR_LABEL"
          />
        </span>
        <!-- A prioridade fica a esquerda do selo de tipo da conversa. O `v-if`
             nao e zelo: sem prioridade o CardPriorityIcon ainda renderiza o
             elemento (quem fica vazio e o `iconName`, nao o `<Icon>`), e ele
             comeria 16px e mais um `gap` nesta linha. -->
        <CardPriorityIcon
          v-if="chat.priority"
          :priority="chat.priority"
          icon-size-class="size-4"
          class="shrink-0 !size-4"
          :class="{ 'cursor-pointer': !isMobileViewport }"
          :role="!isMobileViewport ? 'button' : undefined"
          :tabindex="!isMobileViewport ? 0 : undefined"
          :aria-label="
            !isMobileViewport ? $t('CONVERSATION.PRIORITY.TITLE') : undefined
          "
          @click="openContextMenuFromTrigger($event, 'priority')"
          @keydown.enter="openContextMenuFromTrigger($event, 'priority')"
          @keydown.space="openContextMenuFromTrigger($event, 'priority')"
        />
        <ContactClassificationBadge
          v-if="showOrigemRowContent"
          :classification="contactClassification"
          short
          role="button"
          tabindex="0"
          class="cursor-pointer"
          @click="openContextMenuFromTrigger($event, 'classification')"
          @keydown.enter="openContextMenuFromTrigger($event, 'classification')"
          @keydown.space="openContextMenuFromTrigger($event, 'classification')"
        />
        <!-- Aceleres: o mesmo selo com dois desfechos. O vermelho e o
             #DC2626 da propria etiqueta `venda-perdida` do funil, para os dois
             nao brigarem lado a lado nesta linha. -->
        <span
          v-if="
            showOrigemRowContent && showConcludedStatusBadge && isVendaPerdida
          "
          class="inline-flex items-center rounded-full px-2 h-[20px] leading-none text-[12px] font-semibold text-white bg-[#dc2626]"
        >
          {{ $t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.LOST_SALE_TEXT') }}
        </span>
        <span
          v-else-if="
            showOrigemRowContent && showConcludedStatusBadge && isVendaRealizada
          "
          class="acl-conclusion-badge--sale-won inline-flex items-center rounded-full px-2 h-[20px] leading-none text-[12px] font-semibold text-white bg-[#15803d]"
        >
          {{ $t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.SALE_WON_TEXT') }}
        </span>
        <span
          v-else-if="showOrigemRowContent && showConcludedStatusBadge"
          class="inline-flex items-center rounded-full px-2 h-[20px] leading-none text-[12px] font-semibold text-white bg-n-teal-9"
        >
          {{ $t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT') }}
        </span>
        <CardLabels
          v-if="showOrigemRowContent && showLabelsSection"
          :conversation-labels="visibleConversationLabels"
          :funnel-stage="etapaFunil"
          class="inline-flex"
          @open-label-menu="openContextMenuFromTrigger($event, 'label')"
        >
          <template v-if="hasSlaPolicyId && isCommercialContact" #before>
            <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
          </template>
        </CardLabels>
      </div>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :mobile-sheet="isMobileViewport"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :mobile-sheet="isMobileViewport"
        :status="chat.status"
        :inbox-id="inbox.id"
        :team-id="
          chat.team_id ||
          chat.team?.id ||
          chat.meta?.team?.id ||
          chat.inbox?.team_id ||
          teamId
        "
        :priority="chat.priority"
        :is-pinned="!!chat.is_pinned"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :focused-menu-key="focusedContextMenuKey"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        :active-assignee-tab="activeAssigneeTab"
        :assignee-id="assignee?.id"
        :current-user-id="currentUserId"
        :additional-attributes="chat.additional_attributes"
        :conversation-status="chat.status"
        :inbox-connection-type="inboxConnectionType"
        :inbox-connection-status="chat.inbox_connection_status || ''"
        :inbox-instance-id="chat.inbox_instance_id"
        :can-request-ownership="canRequestOwnership && isCommercialContact"
        :ownership-request-pending="!!pendingOwnershipRequest"
        :contact="currentContact"
        :classification-updating="classificationUpdating"
        :deletable-titles="deletableTitles"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-self="onAssignSelf"
        @start-conversation="onStartConversation"
        @assign-mine="onAssignMine"
        @assign-personal="onAssignPersonal"
        @request-ownership-transfer="onRequestOwnershipTransfer"
        @classifyContact="onClassifyContact"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @create-label="onCreateLabel"
        @delete-label="onRequestDeleteLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @toggle-pin="togglePin"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
    <!-- No body: o cartao vive numa lista virtualizada com `transform`, e
         transform cria bloco de contencao — um modal `fixed` aqui dentro
         abriria colado no cartao em vez de centrado na tela. -->
    <Teleport to="body">
      <woot-modal
        v-model:show="showCreateLabelModal"
        :on-close="() => (showCreateLabelModal = false)"
      >
        <FunilLabelCreateModal
          :account-labels="accountLabels"
          @created="onLabelCreated"
          @close="showCreateLabelModal = false"
        />
      </woot-modal>

      <woot-modal
        v-if="labelToDelete"
        show
        :on-close="() => (labelToDelete = null)"
      >
        <FunilLabelDeleteModal
          :label="labelToDelete"
          @deleted="onLabelDeleted"
          @close="labelToDelete = null"
        />
      </woot-modal>
    </Teleport>
    <AceleresOwnershipTransferModal
      :show="showOwnershipModal"
      :current-owner="currentPortfolioOwner"
      :agents="ownershipAgents"
      :submitting="ownershipSubmitting"
      @close="showOwnershipModal = false"
      @submit="submitOwnershipRequest"
    />
    <AceleresOwnershipTransferModal
      :show="showAttendanceModal"
      kind="atendimento"
      :current-owner="currentAttendant"
      :agents="attendanceAgents"
      :initial-target-assignee-id="attendanceTargetAssigneeId"
      :submitting="attendanceSubmitting"
      @close="showAttendanceModal = false"
      @submit="submitAttendanceRequest"
    />
    <ConversationAvatarViewer
      v-if="isMobileViewport && avatarViewer"
      v-bind="avatarViewer"
      @close="avatarViewer = null"
      @chat="onAvatarOpenChat"
      @contact="onAvatarOpenContact"
    />
  </div>
</template>

<style lang="scss" scoped>
/* Aceleres: a MEDIDA da bolinha da etapa mora no estilo inline
   (`etapaDotStyle`), nao aqui — ver o comentario la. Esta regra so garante o
   alinhamento vertical com os icones vizinhos.

   O nome NAO pode ser `.acl-etapa-dot`: essa classe ja e do modal "Mover de
   etapa" (db/aceleres_dashboard_scripts/DASHBOARD_SCRIPTS.html), que a define
   com 14px. */
.acl-funil-etapa-dot {
  align-self: center;
}

/* Aceleres: etiquetas no card aparecem somente como icones coloridos. */
.acl-origem-row :deep(.label) {
  margin: 0;
  display: inline-flex;
  align-items: center;
}
.acl-origem-row :deep(.label.icon-only) {
  width: 20px;
  height: 20px;
  min-width: 20px;
}
.acl-origem-row :deep(.label.icon-only .label-tag-icon) {
  width: 16px;
  height: 16px;
}
.acl-origem-row :deep(.label.icon-only .label-tag-icon svg) {
  width: 16px;
  height: 16px;
  transform: translateY(1px);
  transform-origin: center;
}
.acl-origem-row :deep(.label .label-color-dot),
.acl-origem-row :deep(.label .label--icon),
.acl-origem-row :deep(.label .label-action--button) {
  display: none;
}
/* Aceleres: seta de acao revelada no hover do cartao (ver o comentario no
   template). `pointer-events: none` garante que o clique atravessa a seta e
   chega ao cartao — ela e desenho, nao alvo.

   Pedido 2026-09-02 (segunda passagem): a seta estava a ESQUERDA da linha do
   alfinete e passou para a DIREITA dele — `inset-inline-start: 100%` em vez de
   `inset-inline-end: 100%`. Como a linha e `justify-end`, o alfinete e o
   contador encostam ao fim dela; comecar a seta no fim da linha poe-a depois
   dos dois, seja qual for a combinacao. O `top: 50%` alinha-a pelo centro da
   linha, que muda de altura conforme o contador aparece ou nao.

   Cabe: a coluna direita esta a 12px da borda do cartao (`right-3`) e a linha
   do virtualizador que embrulha o cartao ainda tem 10px de padding a seguir
   (medido no browser, ver ChatList.vue). A caixa de 14px da seta usa os 12px e
   entra 2px nesses 10px — sem barra horizontal na lista. A tinta do chevron
   ocupa so o meio da caixa (~3,5px transparentes de cada lado), por isso o
   desenho fica inteiro dentro do cartao e nao boia fora do fundo do hover.

   Sem margem de proposito: os ~3,5px transparentes de cada icone ja fazem a
   folga entre o alfinete e a seta. Uma margem de 4px empurraria a tinta para
   fora da borda do cartao.

   Sem `width`/`height` aqui de proposito: a medida vem do `size-3.5` no
   template, o mesmo do alfinete. Escrever 14px tambem aqui criaria uma segunda
   fonte para "do tamanho do alfinete". */
/* Pedido 2026-09-02 (terceira passagem): a seta encostava na borda direita do
   cartao. Comecava no FIM desta linha (`inset-inline-start: 100%`), ou seja em
   cima dos 12px de padding do cartao, e ainda entrava 2px fora dele.

   Agora acaba onde a linha acaba (`inset-inline-end: 0`): a caixa de 14px cai
   exatamente onde o alfinete esta em repouso, portanto ganha a mesma folga da
   borda que o alfinete sempre teve — nao ha numero novo a inventar, e a folga
   fica correta em qualquer largura de lista.

   O espaco para ela sai do hover, nao do repouso: `padding-inline-end` de 14px
   (a largura da propria seta) empurra alfinete e contador para a esquerda so
   enquanto o rato esta em cima. Em repouso o cartao fica exatamente como
   estava — ninguem que nao esteja a apontar ve diferenca. Os ~3,5px
   transparentes de cada icone fazem a folga entre o alfinete e a seta, pelo
   que os 14px sao a largura exata e nao levam margem por cima.

   `:has(.acl-card-arrow)` porque a seta nao existe no telemovel nem em modo de
   selecao: sem a guarda, o hover reservaria espaco para uma seta que nunca
   aparece. */
.acl-card-meta-row {
  transition: padding-inline-end 0.12s ease-in-out;
}

.conversation:hover .acl-card-meta-row:has(.acl-card-arrow) {
  padding-inline-end: 14px;
}

.acl-card-arrow {
  position: absolute;
  inset-inline-end: 0;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease-in-out;
  z-index: 2;
}
.conversation:hover .acl-card-arrow {
  opacity: 1;
}

/* Aceleres: check verde (modo seleção WhatsApp) à esquerda do avatar */
.acl-sel-check {
  flex-shrink: 0;
  align-self: center;
  width: 20px;
  height: 20px;
  margin-right: 20px;
  border-radius: 6px;
  background-color: #ffffff;
  border: 1.8px solid #b8b8b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s,
    color 0.12s;
  padding: 0;
}
.acl-sel-check:hover {
  border-color: #1fad54;
}
.acl-sel-check--on {
  background-color: #1fad54;
  border-color: #1fad54;
  color: #ffffff;
}
.acl-sel-check--on:hover {
  background-color: #189547;
  border-color: #189547;
}

/* Aceleres 2026-09-04: "Digitando..." na linha da previa do cartao.
   O par de verdes e o MESMO ja usado pela lista em ChatList.vue
   (`.acl-archived-row--active`): #0b8457 no claro e #25d366 no escuro. O verde
   de marca puro sobre fundo branco fica em ~1,9:1 e nao passa em texto de
   14px; o escuro so e legivel sobre fundo escuro. */
.acl-typing-preview {
  color: #0b8457;
}

:global(.dark) .acl-typing-preview {
  color: #25d366;
}
</style>
